import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Hero: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });
  const [activeImage, setActiveImage] = useState(0);
  
  useEffect(() => {
    // Auto-rotate images
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % 2);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={elementRef as React.RefObject<HTMLElement>} className="relative bg-gradient-to-br from-[#E8E4DF] via-[#EBE7E2] to-[#E0DCD7] pt-32 md:pt-40 lg:pt-48 pb-20 md:pb-24 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-tr from-white/40 via-transparent to-primary/5 animate-pulse-slow"></div>
      
      {/* Decorative floating elements */}
      <div className="absolute top-20 right-[15%] w-32 h-32 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-32 left-[10%] w-40 h-40 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-3xl animate-float-delay"></div>
      
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* Left side - Images Gallery */}
          <div className={`relative order-1 transition-all duration-1000 ${isVisible ? 'animate-slide-in-left opacity-100' : 'opacity-0 -translate-x-12'}`}>
            <div className="relative">
              {/* Main large image - Clinic interior */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-primary/10 rounded-3xl transform rotate-3 group-hover:rotate-6 transition-transform duration-700"></div>
                <div className="relative bg-white p-3 md:p-4 rounded-3xl shadow-2xl transform transition-all duration-700 hover:scale-[1.02] hover:-rotate-1">
                  <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-gray-100">
                    <img 
                      src="/assets/img/case/b22.jpeg" 
                      alt="Interior da Clínica BotoHarmony" 
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-105"
                    />
                  </div>
                </div>
              </div>
              
              {/* Small floating image - Top left corner */}
              <div className={`absolute -top-8 -left-4 md:-top-12 md:-left-8 w-32 h-32 md:w-44 md:h-44 group transition-all duration-1000 delay-300 ${isVisible ? 'animate-fade-in-delay opacity-100' : 'opacity-0 -translate-y-8'}`}>
                <div className="relative h-full animate-float-delay">
                  <div className="bg-white p-2 md:p-3 rounded-2xl shadow-xl h-full transform transition-all duration-500 hover:scale-110 hover:rotate-3 border border-gray-100">
                    <div className="rounded-xl overflow-hidden h-full bg-gray-100">
                      <img 
                        src="/assets/img/IMG_1697.JPEG" 
                        alt="Recepção da Clínica" 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Experience badge - Bottom right */}
              <div className={`absolute -bottom-6 -right-6 md:-bottom-8 md:-right-8 transition-all duration-1000 delay-500 ${isVisible ? 'animate-fade-in-delay-2 opacity-100' : 'opacity-0 translate-y-8'}`}>
                <div className="relative animate-float">
                  <div className="absolute inset-0 bg-secondary/20 rounded-full blur-xl animate-pulse-slow"></div>
                  <div className="relative w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-white to-gray-50 flex items-center justify-center transform transition-all hover:scale-110 duration-500 group cursor-pointer shadow-2xl border-4 border-white">
                    <div className="text-center">
                      <span className="block text-3xl md:text-4xl font-bold text-secondary group-hover:scale-110 transition-transform duration-300">4</span>
                      <span className="block text-xs md:text-sm text-gray-700 font-semibold mt-1">Anos de</span>
                      <span className="block text-xs md:text-sm text-gray-700 font-semibold">Experiência</span>
                    </div>
                    <div className="absolute inset-0 rounded-full border-2 border-dashed border-secondary/30 animate-rotate-slow"></div>
                  </div>
                </div>
              </div>
              
              {/* Certification badge - Top right */}
              <div className={`absolute top-4 right-4 md:top-6 md:right-6 transition-all duration-1000 delay-700 ${isVisible ? 'animate-fade-in-delay-2 opacity-100' : 'opacity-0 -translate-y-8'}`}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-white/80 rounded-2xl blur-sm"></div>
                  <div className="relative bg-white/95 backdrop-blur-sm px-3 py-2 md:px-4 md:py-3 rounded-2xl shadow-lg border border-gray-200/50 transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group-hover:from-secondary/20 group-hover:to-primary/20 transition-all duration-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5 text-gray-600 group-hover:text-secondary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="text-[10px] md:text-xs font-bold text-gray-700 leading-tight">MEMBRO</p>
                        <p className="text-[8px] md:text-[10px] text-gray-600 leading-tight">American Society</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Content */}
          <div className={`order-2 transition-all duration-1000 ${isVisible ? 'animate-slide-in-right opacity-100' : 'opacity-0 translate-x-12'}`}>
            <div className="max-w-2xl lg:ml-auto">
              {/* Title with gradient effect */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold mb-6 leading-[1.15] group">
                <span className="inline-block bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent animate-fade-in">
                  Naturalidade não é tendência.
                </span>
                <br />
                <span className="inline-block bg-gradient-to-r from-secondary via-primary to-secondary bg-clip-text text-transparent animate-fade-in-delay transform transition-all duration-300 hover:scale-105 inline-block">
                  É assinatura.
                </span>
              </h1>
              
              {/* Description */}
              <div className="space-y-4 mb-8">
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed animate-fade-in-delay">
                  Procedimentos faciais e corporais com sutileza, técnica e resultado real.
                </p>
              </div>
              
              {/* CTA Button */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-fade-in-delay-2">
                <Link 
                  to="/agendar" 
                  className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-dark text-white font-medium text-sm md:text-base tracking-wide overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 border-2 border-dark hover:border-secondary"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-secondary to-primary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 relative z-10 group-hover:scale-110 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="relative z-10 font-semibold">AGENDAR AVALIAÇÃO</span>
                </Link>
              </div>
              
              {/* Info cards */}
              <div className="grid grid-cols-2 gap-4 animate-fade-in-delay-2">
                <div className="group bg-white/60 backdrop-blur-sm p-4 rounded-xl border border-gray-200/50 hover:bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm mb-1">Estrutura Hospitalar</h3>
                      <p className="text-xs text-gray-600">Equipamentos de última geração</p>
                    </div>
                  </div>
                </div>
                
                <div className="group bg-white/60 backdrop-blur-sm p-4 rounded-xl border border-gray-200/50 hover:bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm mb-1">Equipe Experiente</h3>
                      <p className="text-xs text-gray-600">Profissionais qualificados</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary/30 to-transparent"></div>
    </section>
  );
};

export default Hero;